"use client";

import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  Table,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { GoChevronLeft } from "react-icons/go";
import { useNavigate } from "react-router-dom";

const product = {
  name: "Emzor Paracetamol 500mg",
  productName: "Emzor Paracetamol",
  upc: "UPC-123456-12",
  batchNo: "1234578",
  category: "Analgesic",
  dosageForm: "Pills",
  concentration: "500mg",
  manufacturer: "Emzor Pharmaceuticals",
  inferredDiseases: ["Pain relief"],
  stockLevel: "Low Stock",
  prescriptionStatus: "Required",
  restrictionStatus: "Controlled",
  status: "Active",
};

const stockSummary = {
  unitCost: "₦6000.00",
  sellingCost: "₦6500.00",
  stockQuantity: "12,000 units",
  unitMeasure: "Packs",
  initialStock: "Pills",
  minimalStock: "1000 units",
  reorderPoint: "900 units",
  locations: "—",
};

const stockHistory = [
  {
    date: "12th March, 2025",
    location: 13,
    quantity: "15,000 Unit",
    unitCost: "7000",
    sellingCost: "7500 Cost",
    leadTime: "21 days",
  },
  {
    date: "15th April, 2025",
    location: 14,
    quantity: "20,000 Unit",
    unitCost: "8000",
    sellingCost: "8000 Cost",
    leadTime: "25 days",
  },
  {
    date: "18th May, 2025",
    location: 15,
    quantity: "18,500 Unit",
    unitCost: "8500",
    sellingCost: "8200 Cost",
    leadTime: "30 days",
  },
];

export default function ProductDetailsPage() {
  const navigate = useNavigate();
  return (
    <div className=" space-y-6!">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Back"
            variant="ghost">
            <GoChevronLeft />
          </IconButton>
          <Heading size="lg">{product.name}</Heading>
        </div>

        <Button colorScheme="blue" variant="outline">
          Manage Product
        </Button>
      </div>

      <Card.Root>
        <Card.Body className="space-y-6">
          <Heading size="md" mb={4}>
            Basic Details
          </Heading>

          <div className="grid grid-cols-1 md:grid-cols-7 gap-6! text-sm!">
            <Info label="Product Name" value={product.productName} />
            <Info label="UPC" value={product.upc} />
            <Info label="Batch NO" value={product.batchNo} />
            <Info label="Category" value={product.category} />
            <Info label="Dosage Form" value={product.dosageForm} />
            <Info label="Concentration" value={product.concentration} />
            <Info label="Product Manufacturer" value={product.manufacturer} />
          </div>

          <div className="flex flex-wrap gap-4">
            <Badge colorScheme="blue">{product.inferredDiseases[0]}</Badge>
            <Badge colorScheme="orange">{product.stockLevel}</Badge>
            <Badge colorScheme="cyan">{product.prescriptionStatus}</Badge>
            <Badge colorScheme="red">{product.restrictionStatus}</Badge>
            <Badge colorScheme="blue">{product.status}</Badge>
          </div>
        </Card.Body>
      </Card.Root>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card.Root>
          <Card.Body className="space-y-4!">
            <Heading size="md">Current Stock</Heading>

            <StockRow label="Unit Cost" value={stockSummary.unitCost} />
            <StockRow label="Selling Cost" value={stockSummary.sellingCost} />
            <StockRow
              label="Stock Quantity"
              value={stockSummary.stockQuantity}
            />
            <StockRow label="Unit Measure" value={stockSummary.unitMeasure} />
            <StockRow label="Initial Stock" value={stockSummary.initialStock} />
            <StockRow
              label="Minimal Stock Level"
              value={stockSummary.minimalStock}
            />
            <StockRow label="Reorder Point" value={stockSummary.reorderPoint} />
            <StockRow label="Stock Locations" value={stockSummary.locations} />

            <Button colorScheme="blue" variant="outline" width="full">
              Reorder Stock
            </Button>
          </Card.Body>
        </Card.Root>

        {/* Stock History */}
        <Card.Root className="lg:col-span-2">
          <Card.Body>
            <Heading size="md" mb={4}>
              Stock History
            </Heading>

            <Table.Root size="md">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Reorder Date</Table.ColumnHeader>
                  <Table.ColumnHeader>Locations</Table.ColumnHeader>
                  <Table.ColumnHeader>Quantity</Table.ColumnHeader>
                  <Table.ColumnHeader>Unit Cost</Table.ColumnHeader>
                  <Table.ColumnHeader>Selling Cost</Table.ColumnHeader>
                  <Table.ColumnHeader>Lead Time</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {stockHistory.map((row, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{row.date}</Table.Cell>
                    <Table.Cell>{row.location}</Table.Cell>
                    <Table.Cell>{row.quantity}</Table.Cell>
                    <Table.Cell>{row.unitCost}</Table.Cell>
                    <Table.Cell>{row.sellingCost}</Table.Cell>
                    <Table.Cell>{row.leadTime}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>

            <Text mt={4} fontSize="sm" color="gray.500">
              Showing 8 of 2000
            </Text>
          </Card.Body>
        </Card.Root>
      </div>
    </div>
  );
}

/* -------------------- Small Helpers -------------------- */

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontWeight="medium">{value}</Text>
    </Box>
  );
}

function StockRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <Text color="gray.600">{label}:</Text>
      <Text fontWeight="medium">{value}</Text>
    </div>
  );
}
